import React, { useState } from 'react'
import { connect as connectRedux } from 'react-redux'
import { Map } from 'immutable'
import _get from 'lodash/get'
import _snakeCase from 'lodash/snakeCase'
import ButtonToggle from '../../ButtonToggle/index'
import { getBoundActions } from '../../../actions/index'
import { get as getConfig } from '../../../config/index'
import {
  EMAIL_OPT_IN_SETTINGS_ON,
  EMAIL_OPT_IN_SETTINGS_OFF,
  EMAIL_PERMISSION_TOGGLED_GA4_EVENT,
} from '../../../services/event-tracking/index'
import {
  EMAIL_PREFERENCE_FREE_ONLINE,
  EMAIL_PREFERENCE_GAIA_SPHERE,
  EMAIL_PREFERENCE_MARKETPLACE,
  EMAIL_PREFERENCE_NEWSLETTER,
  postBrooklyn as postEmailCapture,
} from '../../../services/email-signup/index'
import Button, { TYPES } from '../../Button/index'
import { BrazePreferenceCenterEmailSettings } from './BrazePreferenceCenterEmailSettings'

const configuration = getConfig()
const showGranularEmailPreferences = !!_get(configuration, ['features', 'granularEmailPreferences'])
const brazeEmailPreferenceCenter = !!_get(configuration, ['features', 'userAccount', 'brazeEmailPreferenceCenter'])

function getGranularPreference (props) {
  const { emarsys, staticText, isSubscribed } = props
  return [
    {
      key: EMAIL_PREFERENCE_NEWSLETTER,
      value: isSubscribed && emarsys.get(_snakeCase(EMAIL_PREFERENCE_NEWSLETTER), false),
      copy: staticText.getIn(['data', 'gaiaNewsletter']),
    },
    {
      key: EMAIL_PREFERENCE_MARKETPLACE,
      value: isSubscribed && emarsys.get(_snakeCase(EMAIL_PREFERENCE_MARKETPLACE), false),
      copy: staticText.getIn(['data', 'gaiaMarketplace']),
    },
    {
      key: EMAIL_PREFERENCE_GAIA_SPHERE,
      value: isSubscribed && emarsys.get(_snakeCase(EMAIL_PREFERENCE_GAIA_SPHERE), false),
      copy: staticText.getIn(['data', 'gaiaSphere']),
    },
    {
      key: EMAIL_PREFERENCE_FREE_ONLINE,
      value: isSubscribed && emarsys.get(_snakeCase(EMAIL_PREFERENCE_FREE_ONLINE), false),
      copy: staticText.getIn(['data', 'freeOnline']),
    },
  ]
}

// new granular email preferences
function renderGranularEmailSetting (option, props) {
  const { copy, value, key } = option
  const {
    auth,
    setEventDefault,
    updateUserEmailPreferences,
  } = props

  const onToggleEmailSubscription = () => {
    const toggledTo = !value
    const eventLabel = toggledTo ? EMAIL_OPT_IN_SETTINGS_ON : EMAIL_OPT_IN_SETTINGS_OFF

    // GA4 EVENT
    setEventDefault(EMAIL_PERMISSION_TOGGLED_GA4_EVENT.mergeDeep({
      changed_to: eventLabel,
      preference: copy,
    }))

    postEmailCapture({
      email: auth.get('email'),
      [key]: toggledTo,
      ...(toggledTo && { optin: true }),
      fields: {},
    })

    updateUserEmailPreferences({
      [_snakeCase(key)]: toggledTo,
      ...(toggledTo && { optin: true }),
    })
  }
  return (
    <div className="my-account-settings-V2__display-settings-controls my-account-settings-V2__display-settings-controls--granular" key={key}>
      <div className="my-account-settings-V2__notification-settings-title">
        {copy}
      </div>
      <div className="my-account-settings-V2__notification-settings-toggle">
        <ButtonToggle
          checked={value}
          onChange={onToggleEmailSubscription}
          showLabel
          round
        />
      </div>
    </div>
  )
}

// Opt out all preferences button
function renderOptOutButton (props) {
  const {
    staticText,
    setEventDefault,
    auth,
    updateUserEmailPreferences,
    isSubscribed,
  } = props

  const optOutEmailPreferences = () => {
    const toggledTo = !isSubscribed
    const eventLabel = toggledTo ? EMAIL_OPT_IN_SETTINGS_ON : EMAIL_OPT_IN_SETTINGS_OFF

    // GA4 EVENT
    setEventDefault(EMAIL_PERMISSION_TOGGLED_GA4_EVENT.mergeDeep({
      changed_to: eventLabel,
      preference: staticText.getIn(['data', 'optOutEmail']),
    }))

    postEmailCapture({
      email: auth.get('email'),
      optin: toggledTo,
      fields: {},
    })

    updateUserEmailPreferences({
      optin: toggledTo,
    })
  }

  return (
    <div className="my-account-settings-V2__display-settings-controls">
      <Button
        type={TYPES.SECONDARY}
        className="my-account-settings-V2__opt-out-btn"
        onClick={optOutEmailPreferences}
      >
        {staticText.getIn(['data', 'optOutEmail'])}
      </Button>
    </div>
  )
}

function EmailSettings (props) {
  const {
    staticText,
    isSubscribed,
    auth,
    unavailable,
    setEventDefault,
  } = props

  const [subscribed, setSubscribed] = useState(isSubscribed)
  const granularPreferences = getGranularPreference(props)

  const onToggleEmailSubscription = () => {
    const optin = !subscribed
    setSubscribed(optin)
    const eventLabel = optin ? EMAIL_OPT_IN_SETTINGS_ON : EMAIL_OPT_IN_SETTINGS_OFF

    // GA4 EVENT
    setEventDefault(EMAIL_PERMISSION_TOGGLED_GA4_EVENT.mergeDeep({
      changed_to: eventLabel,
      preference: staticText.getIn(['data', 'emailSubscription']),
    }))

    postEmailCapture({
      email: auth.get('email'),
      optin,
      fields: {},
    })
  }

  const renderEmailSettingsOptions = showGranularEmailPreferences
    ? (
      <React.Fragment>
        {granularPreferences.map((option) => {
          return renderGranularEmailSetting(option, props)
        })}
        {renderOptOutButton(props, granularPreferences)}
      </React.Fragment>
    ) : (
      <div className="my-account-settings-V2__notification-settings-controls">
      <div className="my-account-settings-V2__notification-settings-text-container">
        <div className="my-account-settings-V2__notification-settings-title">
          {staticText.getIn(['data', 'emailSubscription'])}
        </div>
        <div className="my-account-settings-V2__notification-settings-text">
          {staticText.getIn(['data', 'toggleThis'])}
        </div>
      </div>
      <div className="my-account-settings-V2__notification-settings-toggle">
        <ButtonToggle
          checked={subscribed}
          onChange={onToggleEmailSubscription}
          showLabel
          round
        />
      </div>
    </div>
    )

  if (brazeEmailPreferenceCenter) {
    return (
      <BrazePreferenceCenterEmailSettings
        jwt={auth.get('jwt')}
        manageEmailPreferencesCta={staticText.getIn(['data', 'manageEmailPreferencesCta'], '')}
        preferenceCenterUnavailable={staticText.getIn(['data', 'preferenceCenterUnavailable'], '')}
      />
    )
  }

  return (
    <>
      {unavailable ? (
        <div className="my-account-settings-V2__notification-settings-controls">
          {staticText.getIn(['data', 'emarsysUnavailable'])}
        </div>
      ) : (renderEmailSettingsOptions)}
    </>
  )
}

export default connectRedux(
  (state) => ({
    auth: state.auth,
    isSubscribed: state.user.getIn(['data', 'emarsys', 'optin'], false),
    unavailable: state.user.getIn(['data', 'emarsys', 'unavailable'], false),
    staticText: state.staticText.getIn(['data', 'myAccountSettingsV2'], Map()),
    emarsys: state.user.getIn(['data', 'emarsys'], Map()),
  }),
  (dispatch) => {
    const actions = getBoundActions(dispatch)
    return {
      setEventDefault: actions.eventTracking.setEventDefault,
      updateUserEmailPreferences: actions.user.updateUserEmailPreferences,
    }
  },
)(EmailSettings)
